import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { filterCandidateMarks } from "./filterMask.utils";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  // Sequence Range Filter
  sequenceFrom?: number | null;
  sequenceTo?: number | null;
  minAvailableSequence?: number;
  maxAvailableSequence?: number;
  onSequenceRangeChange?: (range: { from: number | null; to: number | null }) => void;
  // Mark Filter (Mask based)
  availableMarks?: string[];
  markMask?: string;
  onMarkMaskChange?: (mask: string, matchingMarks: string[]) => void;
  // Legacy / Direct mark selection for compatibility
  selectedMarks?: string[];
  onMarkSelectionChange?: (selectedMarks: string[]) => void;
  // Type Filter
  availableTypes?: string[];
  selectedTypes?: string[];
  onTypeSelectionChange?: (selectedTypes: string[]) => void;
}

const ITEMS_PER_BATCH = 20;

const FilterDrawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  sequenceFrom = null,
  sequenceTo = null,
  minAvailableSequence,
  maxAvailableSequence,
  onSequenceRangeChange,
  availableMarks = [],
  markMask = "",
  onMarkMaskChange,
  selectedMarks = [],
  onMarkSelectionChange,
  availableTypes = [],
  selectedTypes = [],
  onTypeSelectionChange,
}) => {
  // Local state for Sequence Range
  const [localSequenceFrom, setLocalSequenceFrom] = useState<string>(
    sequenceFrom !== null && sequenceFrom !== undefined ? String(sequenceFrom) : ""
  );
  const [localSequenceTo, setLocalSequenceTo] = useState<string>(
    sequenceTo !== null && sequenceTo !== undefined ? String(sequenceTo) : ""
  );

  // Local state for Mark Mask
  const [localMarkMask, setLocalMarkMask] = useState<string>(markMask || "");

  // Local state for Types
  const [localSelectedTypes, setLocalSelectedTypes] = useState<string[]>(selectedTypes);
  const [typeSearchQuery, setTypeSearchQuery] = useState("");
  const [visibleTypesCount, setVisibleTypesCount] = useState(ITEMS_PER_BATCH);

  const typeListRef = useRef<HTMLUListElement>(null);

  // Sync state when drawer opens or initial props change
  useEffect(() => {
    if (open) {
      setLocalSequenceFrom(
        sequenceFrom !== null && sequenceFrom !== undefined ? String(sequenceFrom) : ""
      );
      setLocalSequenceTo(
        sequenceTo !== null && sequenceTo !== undefined ? String(sequenceTo) : ""
      );
      setLocalMarkMask(markMask || "");
      setLocalSelectedTypes(selectedTypes);
      setTypeSearchQuery("");
      setVisibleTypesCount(ITEMS_PER_BATCH);
    }
  }, [open, sequenceFrom, sequenceTo, markMask, selectedTypes]);

  // Candidate marks matching current mask
  const matchingCandidateMarks = useMemo(() => {
    return filterCandidateMarks(availableMarks, localMarkMask);
  }, [availableMarks, localMarkMask]);

  // Types list filtering
  const uniqueTypes = useMemo(() => availableTypes, [availableTypes]);

  const filteredTypes = useMemo(() => {
    if (!typeSearchQuery.trim()) return uniqueTypes;
    const query = typeSearchQuery.toLowerCase();
    return uniqueTypes.filter((type) => type.toLowerCase().includes(query));
  }, [uniqueTypes, typeSearchQuery]);

  useEffect(() => {
    setVisibleTypesCount(ITEMS_PER_BATCH);
  }, [typeSearchQuery]);

  const handleTypeScroll = useCallback(() => {
    const listElement = typeListRef.current;
    if (!listElement) return;

    const { scrollTop, scrollHeight, clientHeight } = listElement;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

    if (scrollPercentage > 0.8 && visibleTypesCount < filteredTypes.length) {
      setVisibleTypesCount((prev) => Math.min(prev + ITEMS_PER_BATCH, filteredTypes.length));
    }
  }, [visibleTypesCount, filteredTypes.length]);

  const handleTypeToggle = (type: string) => {
    const newSelection = localSelectedTypes.includes(type)
      ? localSelectedTypes.filter((t) => t !== type)
      : [...localSelectedTypes, type];
    setLocalSelectedTypes(newSelection);
  };

  const handleApply = () => {
    // 1. Sequence Range
    const fromNum = localSequenceFrom.trim() !== "" ? Number(localSequenceFrom) : null;
    const toNum = localSequenceTo.trim() !== "" ? Number(localSequenceTo) : null;
    onSequenceRangeChange?.({ from: fromNum, to: toNum });

    // 2. Mark Mask
    onMarkMaskChange?.(localMarkMask, matchingCandidateMarks);
    onMarkSelectionChange?.(matchingCandidateMarks);

    // 3. Types
    onTypeSelectionChange?.(localSelectedTypes);

    onClose();
  };

  const handleClear = () => {
    setLocalSequenceFrom("");
    setLocalSequenceTo("");
    setLocalMarkMask("");
    setLocalSelectedTypes([]);

    onSequenceRangeChange?.({ from: null, to: null });
    onMarkMaskChange?.("", availableMarks);
    onMarkSelectionChange?.([]);
    onTypeSelectionChange?.([]);
  };

  const isMaskActive = localMarkMask.trim() !== "";
  const candidateCount = matchingCandidateMarks.length;

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{ "& .MuiDrawer-paper": { width: 340, p: 2 } }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="flex-start" alignItems="center" mb={2}>
        <IconButton onClick={onClose} edge="start" sx={{ mr: 0.5 }}>
          <CloseIcon />
        </IconButton>
        <Typography variant="body2" fontWeight={600}>
          CLOSE
        </Typography>
      </Box>

      {/* Filter Title */}
      <Typography variant="subtitle1" fontWeight={600} mb={2}>
        Filter
      </Typography>

      {/* 1. Sequence Range Section (TOP) */}
      <Box mb={2.5}>
        <Typography variant="body2" fontWeight={600} mb={1}>
          Sequence Range
        </Typography>
        <Box display="flex" gap={1}>
          <TextField
            size="small"
            label="From"
            type="number"
            placeholder={minAvailableSequence !== undefined ? String(minAvailableSequence) : "Min"}
            value={localSequenceFrom}
            onChange={(e) => setLocalSequenceFrom(e.target.value)}
            inputProps={{ min: 0 }}
            fullWidth
          />
          <TextField
            size="small"
            label="To"
            type="number"
            placeholder={maxAvailableSequence !== undefined ? String(maxAvailableSequence) : "Max"}
            value={localSequenceTo}
            onChange={(e) => setLocalSequenceTo(e.target.value)}
            inputProps={{ min: 0 }}
            fullWidth
          />
        </Box>
        {minAvailableSequence !== undefined && maxAvailableSequence !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            Available sequence: {minAvailableSequence} – {maxAvailableSequence}
          </Typography>
        )}
      </Box>

      {/* 2. Mark Section (MIDDLE - Mask with Candidate Count) */}
      <Box mb={2.5}>
        <Typography variant="body2" fontWeight={600} mb={1}>
          Mark
        </Typography>
        <TextField
          size="small"
          placeholder="e.g. *AB* or AB"
          value={localMarkMask}
          onChange={(e) => setLocalMarkMask(e.target.value)}
          fullWidth
          helperText={
            !isMaskActive
              ? `All marks included (${availableMarks.length} total)`
              : `${candidateCount} candidate${candidateCount === 1 ? "" : "s"} found`
          }
          FormHelperTextProps={{
            sx: {
              fontSize: "0.8rem",
              color: isMaskActive && candidateCount === 0 ? "error.main" : "text.secondary",
              fontWeight: isMaskActive ? 600 : 400,
              mt: 0.5,
            },
          }}
        />
      </Box>

      {/* 3. Type Section (BOTTOM - Last before buttons) */}
      <Box mb={2}>
        <Typography variant="body2" fontWeight={600} mb={1}>
          Type {uniqueTypes.length > 0 && `(${localSelectedTypes.length}/${uniqueTypes.length})`}
        </Typography>
        <TextField
          size="small"
          placeholder="Search types..."
          value={typeSearchQuery}
          onChange={(e) => setTypeSearchQuery(e.target.value)}
          fullWidth
          sx={{ mb: 1 }}
        />
        <List
          ref={typeListRef}
          sx={{ overflow: "auto", maxHeight: "25vh", mb: 1 }}
          onScroll={handleTypeScroll}
        >
          {filteredTypes.length > 0 ? (
            filteredTypes.slice(0, visibleTypesCount).map((type) => (
              <ListItem
                key={type}
                sx={{ pl: 0, py: 0.5, cursor: "pointer" }}
                disablePadding
                onClick={() => handleTypeToggle(type)}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Checkbox
                    checked={localSelectedTypes.includes(type)}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText primary={type} />
              </ListItem>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
              {typeSearchQuery ? "No matching types" : "No types available"}
            </Typography>
          )}
          {visibleTypesCount < filteredTypes.length && (
            <ListItem sx={{ pl: 0, py: 1 }}>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ width: "100%" }}>
                Loading more...
              </Typography>
            </ListItem>
          )}
        </List>
      </Box>

      {/* Footer Buttons */}
      <Box mt="auto" display="flex" flexDirection="column" gap={1}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleApply}
          sx={{ textTransform: "none" }}
        >
          APPLY
        </Button>
        <Button
          variant="outlined"
          onClick={handleClear}
          sx={{ textTransform: "none" }}
        >
          CLEAR
        </Button>
      </Box>
    </Drawer>
  );
};

export { FilterDrawer };
